import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, SlidersHorizontal, Clock, Users, Briefcase, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import Wrapper from "@/components/shared/Wrapper";
import { useUser, useToken } from "@/stores/authStore";
import { useGet } from "@/api/useApi";
import DashboardKpiCard from "../components/DashboardKpiCard";
import DashboardSourcingChart from "../components/DashboardSourcingChart";
import DashboardInsightsPanel from "../components/DashboardInsightsPanel";
import DashboardQuickActions from "../components/DashboardQuickActions";
import DashboardPositionsTable from "../components/DashboardPositionsTable";

const Dashboard = () => {
  const { t } = useTranslation();
  const user = useUser();
  const token = useToken();
  const [viewIdx, setViewIdx] = useState(0);
  const today = format(new Date(), "MMMM, yyyy");

  const viewLabels = useMemo(
    () => [t("dashboardPage.chart.sourcingReports"), t("dashboardPage.chart.insights")],
    [t],
  );

  const ready = !!token && !!user?.companyId;

  /** Agency KPI must use v2 search (agency_id scope); GET /positions only matches shell company_id. */
  const positionsCountReady =
    ready && (!user?.isAgency || Boolean(user?.agencyId));
  const positionsCountUrl =
    user?.isAgency && user?.agencyId
      ? `companies/${user.companyId}/search/agencies/${user.agencyId}`
      : `companies/${user?.companyId}/positions`;
  const positionsCountParams =
    user?.isAgency && user?.agencyId
      ? { resource: "positions", jobStatus: "active", pageSize: 1 }
      : { action: "active" };

  const { data: candidatesData } = useGet(
    "dashboard-candidates-count",
    `companies/${user?.companyId}/search?resource=candidates`,
    { pageSize: 1 },
    { enabled: ready },
  );

  const { data: positionsData } = useGet(
    "dashboard-positions-count",
    positionsCountUrl,
    positionsCountParams,
    { enabled: positionsCountReady },
  );

  const totalCandidates =
    candidatesData?.data?.total ?? candidatesData?.data?.items?.length ?? "—";
  const activePositions =
    positionsData?.data?.total ?? positionsData?.data?.items?.length ?? "—";

  const welcomeFirst =
    typeof user?.fullname === "string" ? user.fullname.trim().split(/\s+/)[0] ?? "" : "";
  const welcomeNameSuffix = welcomeFirst ? ` ${welcomeFirst}` : "";

  return (
    <div className="flex flex-col gap-6 pb-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">
          {t("dashboardPage.home.welcomeBack", { firstName: welcomeNameSuffix })}
        </h1>
        <div className="flex items-center gap-2 text-sm text-secondary-foreground">
          <span>{t("dashboardPage.home.dateLabel")}</span>
          <span className="font-semibold text-foreground">{today}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <DashboardKpiCard
          value={totalCandidates}
          label={t("dashboardPage.kpis.totalCandidates")}
          icon={Users}
        />
        <DashboardKpiCard value="—" label={t("dashboardPage.kpis.activeClients")} icon={Clock} />
        <DashboardKpiCard
          value={activePositions}
          label={t("dashboardPage.kpis.activePositions")}
          icon={Briefcase}
        />
        <DashboardKpiCard
          value="—"
          label={t("dashboardPage.kpis.onlineInterviews")}
          icon={Video}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <Wrapper className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground">{viewLabels[viewIdx]}</h3>
            <div className="flex items-center gap-2">
              {viewIdx === 0 && (
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs text-secondary-foreground border border-[#E5E7EB] px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SlidersHorizontal size={12} />
                  {t("dashboardPage.chart.filterByClients")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setViewIdx((i) => (i + 1) % viewLabels.length)}
                className="flex items-center gap-1.5 text-xs font-medium text-secondary-foreground border border-[#E5E7EB] px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {viewLabels[viewIdx]}
                <ChevronDown size={12} />
              </button>
            </div>
          </div>

          {viewIdx === 0 ? <DashboardSourcingChart /> : <DashboardInsightsPanel />}

          <div className="flex items-center justify-center gap-1.5 mt-4">
            {viewLabels.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`${viewLabels[i]} view`}
                onClick={() => setViewIdx(i)}
                className={`rounded-full transition-all ${
                  i === viewIdx ? "w-4 h-2 bg-primary" : "w-2 h-2 bg-[#D1D5DB]"
                }`}
              />
            ))}
          </div>
        </Wrapper>

        <DashboardQuickActions />
      </div>

      <DashboardPositionsTable />
    </div>
  );
};

export default Dashboard;
